import {ciPlatform, CIMetadata} from '@shopify/cli-kit/node/context/local';
import {getLatestGitCommit} from '@shopify/cli-kit/node/git';
import {Logger, outputWarn} from '@shopify/cli-kit/node/output';

import {maxLabelLength} from '../utils/utils.js';

import {DeploymentConfig, EnvironmentInput} from './types.js';

export type Metadata = CIMetadata & {
  commitDate?: string;
  hydrogenVersion?: string;
  name?: string;
};

export async function getMetadata(
  config: DeploymentConfig,
  logger: Logger,
): Promise<Metadata> {
  const ciInfo = ciPlatform();
  const metadata: Metadata = ciInfo.metadata ?? {};

  try {
    const gitCommit = await latestCommit(config.rootPath);

    // Only update actor, commitDate, and commitMessage if we don't already have
    // the data from CI.
    if (!metadata.actor) {
      metadata.actor = gitCommit.author_name;
    }
    if (!metadata.commitDate) {
      metadata.commitDate = gitCommit.date;
    }
    if (!metadata.commitMessage) {
      metadata.commitMessage = gitCommit.message;
    }

    // Always update the following keys with the Git information if it's
    // available since we may modify what the values are.
    if (gitCommit.branch) {
      metadata.branch = gitCommit.branch;
    }
    if (gitCommit.hash) {
      metadata.commitSha = gitCommit.hash;
    }
  } catch (error) {
    outputWarn(
      'Could not retrieve Git history, commit message will be unavailable.',
      logger,
    );
  }

  return {
    name: ciInfo.isCI ? ciInfo.name : 'none',
    ...metadata,
    actor: config.metadata.user ?? metadata.actor,
    commitMessage: createCommitTitle(
      config.metadata.description ?? metadata.commitMessage,
    ),
    commitSha: config.metadata.version ?? metadata.commitSha,
    hydrogenVersion: config.metadata.hydrogenVersion,
    url: config.metadata.url ?? metadata.url,
  };
}

async function latestCommit(path?: string) {
  const gitCommit = await getLatestGitCommit(path);
  const branch = (/HEAD -> ([^,]*)/.exec(gitCommit.refs) || [])[1];

  // If this variable is set it means we are in a GitHub action using the
  // pull_request trigger which creates a merge commit and we'll need to
  // manually extract the commit details.
  // eslint-disable-next-line no-process-env
  const headRef = process.env.GITHUB_HEAD_REF;

  if (!headRef) {
    return {
      ...gitCommit,
      branch,
    };
  }

  const match = /Merge ([\w\d]+) into [\w\d]+/.exec(gitCommit.message);

  return {
    ...gitCommit,
    branch: headRef,
    hash: match ? match[1] : gitCommit.hash,
  };
}

function createCommitTitle(commitMessage?: string): string | undefined {
  if (!commitMessage) {
    return;
  }

  let title = commitMessage.split(/\r?\n/)[0]?.trim() || '';
  let jsonTitle = JSON.stringify(title);
  let isTruncated = false;

  while (jsonTitle.length > maxLabelLength) {
    const lastSpaceIndex = title.lastIndexOf(' ', maxLabelLength - 3);
    title = `${title.substring(
      0,
      lastSpaceIndex > -1 ? lastSpaceIndex : maxLabelLength - 3,
    )}`.trimEnd();
    isTruncated = true;
    jsonTitle = JSON.stringify(title);
  }

  return isTruncated ? `${title}...` : title;
}

export function getEnvironmentInput(
  config: DeploymentConfig,
  metadata: CIMetadata,
): EnvironmentInput | undefined {
  if (config.defaultEnvironment) {
    return undefined;
  }

  const tag = config.environmentTag || metadata.branch;
  return tag ? {tag} : undefined;
}

export function createLabels(metadata: Metadata): string[] {
  const labels: string[] = [];

  const checkLabel = (labelKey: string, labelValue: string) => {
    const jsonString = JSON.stringify(labelValue);
    if (jsonString.length > maxLabelLength) {
      throw new Error(
        `Provided ${labelKey} metadata exceeds maximum length (max ${maxLabelLength} characters).`,
      );
    }
    const label = `${labelKey}=${jsonString}`;
    labels.push(label);
  };

  if (metadata.name !== 'none') {
    labels.push(`ci-platform=${JSON.stringify(metadata.name)}`);
  }

  if (metadata.name !== 'unknown') {
    const keyMapping = {
      attempt: 'attempt',
      run: 'runId',
    };

    for (const [key, value] of Object.entries(keyMapping)) {
      const metadataKey = key as keyof Metadata;
      if (metadata[metadataKey]) {
        labels.push(
          `${metadata.name}-${value}=${JSON.stringify(metadata[metadataKey])}`,
        );
      }
    }
  }

  const keyMapping = {
    actor: 'user',
    branch: 'branch',
    commitDate: 'commit-date',
    commitMessage: 'description',
    commitSha: 'version',
    hydrogenVersion: 'hydrogen-version',
    url: 'url',
  };

  for (const [key, value] of Object.entries(keyMapping)) {
    const metadataKey = key as keyof Metadata;
    if (metadata[metadataKey]) {
      checkLabel(value, metadata[metadataKey]!);
    }
  }

  return labels;
}
